/* Ardor Printing DTF transfers: background removal for AI designs (window.ArdorDtfCutout). New Oct 14, 2026 (DTF-AI).
Used by the AI T-Shirt Design Generator page (assets/ardor-dtf-idea.js). Meant for the DTF transfer designer too (its AI drawer
and the ?ai= hand-off), so the page and the designer make the SAME transparent art from the same AI image. No libraries.
Same pixels + same options = same result.
How it works: the worker's sticker routes send transparent PNGs (checked Oct 14: 1024 x 1024, art alpha up to 254, soft dark
edges, no light fringe). Such an image keeps its own alpha: near-opaque px become fully opaque (opts.snap), tiny specks are
dropped and the art is trimmed. If an image comes on a plain backdrop instead (for example a file the customer uploads), we
read the colour around the edge of the image. If the edge is plain,
every pixel connected to the edge that is close to that colour becomes transparent. The 1-2 px boundary gets a soft alpha
ramp (feathered edge) and the backdrop colour is taken out of those pixels, so there is no light halo on a dark shirt.
Optional: pockets of the backdrop colour enclosed by the art (inside letters, between arms) are cleared too (opts.holes).
Tiny leftover specks are dropped, then the art is trimmed to its edges.
API
  ArdorDtfCutout.cut(source, opts) -> Promise<result>
    source: Blob | File | ImageBitmap | HTMLImageElement | HTMLCanvasElement | URL string (fetched with CORS)
    opts: see DEFAULTS (all optional)
    result: { canvas (trimmed, transparent PNG-ready), w, h, srcW, srcH, bbox:{x,y,w,h} (in source px), plain, hadAlpha,
              removed (share of source px made transparent), ref:[r,g,b] (backdrop colour), uniform (share of edge px
              that match it), semi (px with soft alpha), opts }
  ArdorDtfCutout.cutImageData(imageData, opts) -> stats (works in place, no trim)
  ArdorDtfCutout.alphaStats(canvas) -> { total, transparent, opaque, semi, corners:[a,a,a,a] }
  ArdorDtfCutout.DEFAULTS, ArdorDtfCutout.version */
(function () {
  'use strict';
  if (window.ArdorDtfCutout && window.ArdorDtfCutout.version >= 1) return;

  var DEFAULTS = {
    lo: 22,          // colour distance (RGB, 0..441) at or below which a backdrop pixel is fully transparent
    mid: 44,         // the backdrop region grows through pixels up to this distance
    hi: 96,          // boundary pixels between lo and hi get a soft alpha ramp
    band: 2,         // boundary width in px that may get the soft ramp
    soft: true,      // also clear soft shadows / gradients of the backdrop colour connected to the edge
    softStep: 6,     // ...entered only in steps of at most this much per channel from px to px
    softMax: 200,    // ...and only up to this colour distance from the backdrop
    feather: 1,      // 1 = light 3x3 smoothing of the alpha edge, 0 = none
    holes: false,    // also clear enclosed pockets of the backdrop colour
    holeMin: 0.0006, // smallest pocket cleared, as a share of the image area
    speck: 0.00003,  // opaque islands smaller than this share of the image area are dropped (min 6 px)
    minUniform: 0.6, // the edge counts as a plain backdrop when this share of edge px match the backdrop colour
    snap: 250,       // images that are already transparent: alpha at or above this becomes 255
    trim: true,
    pad: 2,          // px kept around the art after trimming
    maxSide: 0,      // downscale bigger images to this many px on the long side first (0 = keep)
    scale: 1         // enlarge the finished art (e.g. 3 for print use; smooth interpolation, no new detail)
  };

  function opt(o) {
    var r = {}, k;
    for (k in DEFAULTS) r[k] = DEFAULTS[k];
    if (o) for (k in o) if (Object.prototype.hasOwnProperty.call(DEFAULTS, k) && o[k] != null) r[k] = o[k];
    return r;
  }

  function mk(w, h) { var c = document.createElement('canvas'); c.width = w; c.height = h; return c; }
  function ctx2d(c) { return c.getContext('2d', { willReadFrequently: true }); }

  // ---------- source -> drawable ----------
  function fromBlob(b) {
    if (window.createImageBitmap) return createImageBitmap(b).catch(function () { return imgFromBlob(b); });
    return imgFromBlob(b);
  }
  function imgFromBlob(b) {
    return new Promise(function (res, rej) {
      var u = URL.createObjectURL(b), im = new Image();
      im.onload = function () { res(im); setTimeout(function () { URL.revokeObjectURL(u); }, 0); };
      im.onerror = function () { URL.revokeObjectURL(u); rej(new Error('image decode failed')); };
      im.src = u;
    });
  }
  function load(src) {
    if (typeof src === 'string') {
      return fetch(src, { mode: 'cors', credentials: 'omit' }).then(function (r) {
        if (!r.ok) throw new Error('image fetch ' + r.status);
        return r.blob();
      }).then(fromBlob);
    }
    if (window.Blob && src instanceof Blob) return fromBlob(src);
    return Promise.resolve(src);
  }
  function sizeOf(s) {
    return { w: s.naturalWidth || s.videoWidth || s.width || 0, h: s.naturalHeight || s.videoHeight || s.height || 0 };
  }

  // ---------- core ----------
  function ring(w, h, k) {
    var out = [], x, y, t;
    for (t = 0; t < k; t++) {
      for (x = t; x < w - t; x++) { out.push(t * w + x); out.push((h - 1 - t) * w + x); }
      for (y = t + 1; y < h - 1 - t; y++) { out.push(y * w + t); out.push(y * w + (w - 1 - t)); }
    }
    return out;
  }

  // backdrop colour = the most common colour on the edge (coarse histogram), refined by averaging the matching edge px
  function backdrop(d, edge) {
    var hist = new Uint32Array(4096), i, p, best = 0, bi = 0;
    for (i = 0; i < edge.length; i++) {
      p = edge[i] * 4;
      if (d[p + 3] < 128) continue;
      var b = ((d[p] >> 4) << 8) | ((d[p + 1] >> 4) << 4) | (d[p + 2] >> 4);
      hist[b]++;
    }
    for (i = 0; i < 4096; i++) if (hist[i] > best) { best = hist[i]; bi = i; }
    var c = [((bi >> 8) & 15) * 16 + 8, ((bi >> 4) & 15) * 16 + 8, (bi & 15) * 16 + 8];
    var n = 0, r = 0, g = 0, bl = 0;
    for (i = 0; i < edge.length; i++) {
      p = edge[i] * 4;
      if (d[p + 3] < 128) continue;
      var dr = d[p] - c[0], dg = d[p + 1] - c[1], db = d[p + 2] - c[2];
      if (dr * dr + dg * dg + db * db <= 24 * 24) { n++; r += d[p]; g += d[p + 1]; bl += d[p + 2]; }
    }
    return n ? [r / n, g / n, bl / n] : c;
  }

  // clear 8-connected islands of alpha > 24 that are smaller than opts.speck of the image area (min 6 px)
  function despeck(d, w, h, o) {
    var n = w * h, minSpeck = Math.max(6, Math.round(n * o.speck)), lab = new Uint8Array(n), list = new Int32Array(n);
    var s, p, x, xx, q, dx, dy, t, ln, ls;
    for (s = 0; s < n; s++) {
      if (lab[s] || d[s * 4 + 3] <= 24) continue;
      ln = 0; ls = 0; list[ln++] = s; lab[s] = 1;
      while (ls < ln) {
        p = list[ls++]; x = p % w;
        for (dy = -1; dy <= 1; dy++) for (dx = -1; dx <= 1; dx++) {
          xx = x + dx; q = p + dy * w + dx;
          if ((!dx && !dy) || xx < 0 || xx >= w || q < 0 || q >= n || lab[q] || d[q * 4 + 3] <= 24) continue;
          lab[q] = 1; list[ln++] = q;
        }
      }
      if (ln < minSpeck) for (t = 0; t < ln; t++) d[list[t] * 4 + 3] = 0;
    }
  }

  function cutImageData(img, o) {
    o = opt(o);
    var w = img.width, h = img.height, d = img.data, n = w * h, i, p, k;
    var edge = ring(w, h, Math.min(2, Math.max(1, Math.floor(Math.min(w, h) / 4))));
    var stats = { w: w, h: h, plain: false, hadAlpha: false, removed: 0, ref: null, uniform: 0, semi: 0 };
    if (!n) return stats;

    // already transparent around the edge (the sticker routes send transparent PNGs, art alpha up to 254): keep the
    // image's own alpha, make near-opaque px fully opaque, drop tiny specks
    var clear = 0;
    for (i = 0; i < edge.length; i++) if (d[edge[i] * 4 + 3] < 16) clear++;
    if (clear / edge.length >= 0.6) {
      stats.hadAlpha = true; stats.plain = true;
      for (p = 0; p < n; p++) { i = p * 4 + 3; if (d[i] >= o.snap) d[i] = 255; }
      despeck(d, w, h, o);
      for (p = 0; p < n; p++) { k = d[p * 4 + 3]; if (k === 0) stats.removed++; else if (k < 255) stats.semi++; }
      stats.removed /= n;
      return stats;
    }

    var ref = backdrop(d, edge), R = ref[0], G = ref[1], B = ref[2];
    stats.ref = [Math.round(R), Math.round(G), Math.round(B)];
    var dist = new Float32Array(n);
    for (p = 0; p < n; p++) {
      i = p * 4;
      var dr = d[i] - R, dg = d[i + 1] - G, db = d[i + 2] - B;
      dist[p] = d[i + 3] < 8 ? 0 : Math.sqrt(dr * dr + dg * dg + db * db);
    }
    var near = 0;
    for (i = 0; i < edge.length; i++) if (dist[edge[i]] <= o.mid) near++;
    stats.uniform = near / edge.length;
    if (stats.uniform < o.minUniform) return stats; // busy edge: no plain backdrop, leave the image as it is
    stats.plain = true;

    // 1. backdrop region: flood from the edge through px within `mid`; with opts.soft also through soft shadows and
    //    gradients of the backdrop (px that are a darker or lighter shade of the backdrop colour, entered only in small
    //    steps from a backdrop neighbour, so sharp art edges stop the flood)
    var shadowy = null;
    if (o.soft) {
      shadowy = new Uint8Array(n);
      var bb = R * R + G * G + B * B || 1;
      for (p = 0; p < n; p++) {
        i = p * 4;
        if (dist[p] > o.softMax) continue;
        var sc0 = (d[i] * R + d[i + 1] * G + d[i + 2] * B) / bb, e0 = d[i] - sc0 * R, e1 = d[i + 1] - sc0 * G, e2 = d[i + 2] - sc0 * B;
        if (sc0 >= 0.3 && sc0 <= 1.1 && e0 * e0 + e1 * e1 + e2 * e2 <= 14 * 14) shadowy[p] = 1;
      }
    }
    var bg = new Uint8Array(n), stack = new Int32Array(n), sp = 0, x;
    function can(q, p0) {
      if (bg[q]) return false;
      if (dist[q] <= o.mid) return true;
      if (!shadowy || !shadowy[q] || !(shadowy[p0] || dist[p0] <= o.mid)) return false;
      var qi = q * 4, pi = p0 * 4;
      return Math.abs(d[qi] - d[pi]) <= o.softStep && Math.abs(d[qi + 1] - d[pi + 1]) <= o.softStep && Math.abs(d[qi + 2] - d[pi + 2]) <= o.softStep;
    }
    function flood() {
      while (sp) {
        p = stack[--sp]; x = p % w;
        if (x > 0 && can(p - 1, p)) { bg[p - 1] = 1; stack[sp++] = p - 1; }
        if (x < w - 1 && can(p + 1, p)) { bg[p + 1] = 1; stack[sp++] = p + 1; }
        if (p >= w && can(p - w, p)) { bg[p - w] = 1; stack[sp++] = p - w; }
        if (p < n - w && can(p + w, p)) { bg[p + w] = 1; stack[sp++] = p + w; }
      }
    }
    for (i = 0; i < edge.length; i++) { p = edge[i]; if (!bg[p] && dist[p] <= o.mid) { bg[p] = 1; stack[sp++] = p; } }
    flood();

    // 2. optional: enclosed pockets of (almost exactly) the backdrop colour
    if (o.holes) {
      var seen = new Uint8Array(n), comp = new Int32Array(n), minHole = Math.max(12, Math.round(n * o.holeMin));
      for (var s = 0; s < n; s++) {
        if (bg[s] || seen[s] || dist[s] > o.lo) continue;
        var cn = 0, cs = 0; comp[cn++] = s; seen[s] = 1;
        while (cs < cn) {
          p = comp[cs++]; x = p % w;
          var nb = [x > 0 ? p - 1 : -1, x < w - 1 ? p + 1 : -1, p >= w ? p - w : -1, p < n - w ? p + w : -1];
          for (var t = 0; t < 4; t++) { var q = nb[t]; if (q >= 0 && !seen[q] && !bg[q] && dist[q] <= o.lo) { seen[q] = 1; comp[cn++] = q; } }
        }
        if (cn >= minHole) { for (t = 0; t < cn; t++) { q = comp[t]; bg[q] = 1; stack[sp++] = q; } flood(); }
      }
    }

    // 3. alpha. Backdrop px more than `band` px away from the art: 0. Px on the boundary (backdrop side or art side, within
    //    `band` px): coverage against the art colour F within 2 px that best explains the px as a mix of F and the backdrop B,
    //    alpha = (C-B).(F-B) / |F-B|^2 (a 50 % black-on-white edge px gets 0.5). An art px that is no such mix keeps 1.
    //    With no strong art colour near (pale art), a ramp lo..hi on the distance to the backdrop. Everything else: 1.
    function dilate(m, steps) {
      var cur = m, s0, q0, nx0;
      for (s0 = 0; s0 < steps; s0++) {
        nx0 = new Uint8Array(cur);
        for (q0 = 0; q0 < n; q0++) {
          if (!cur[q0]) continue;
          var x0 = q0 % w;
          if (x0 > 0) nx0[q0 - 1] = 1; if (x0 < w - 1) nx0[q0 + 1] = 1;
          if (q0 >= w) nx0[q0 - w] = 1; if (q0 < n - w) nx0[q0 + w] = 1;
        }
        cur = nx0;
      }
      return cur;
    }
    var fg = new Uint8Array(n);
    for (p = 0; p < n; p++) fg[p] = bg[p] ? 0 : 1;
    var band = Math.max(1, o.band | 0), nearFg = dilate(fg, band), nearBg = dilate(bg, band);
    var span = Math.max(1, o.hi - o.lo), alpha = new Float32Array(n), hi2 = o.hi;
    function ramp(v) { return Math.min(1, Math.max(0, (v - o.lo) / span)); }
    function estimate(q) {
      if (dist[q] <= o.lo) return 0;
      var qx = q % w, qy = (q - qx) / w, yy, xx2, r2, fi, fr, fgc, fb, f2, a2, e1, e2, e3, res;
      var qi = q * 4, cr = d[qi] - R, cg = d[qi + 1] - G, cb = d[qi + 2] - B, bestA = -1, bestRes = 1e12;
      for (yy = Math.max(0, qy - 2); yy <= Math.min(h - 1, qy + 2); yy++) {
        for (xx2 = Math.max(0, qx - 2); xx2 <= Math.min(w - 1, qx + 2); xx2++) {
          r2 = yy * w + xx2;
          if (r2 === q || bg[r2] || dist[r2] < hi2) continue;
          fi = r2 * 4; fr = d[fi] - R; fgc = d[fi + 1] - G; fb = d[fi + 2] - B; f2 = fr * fr + fgc * fgc + fb * fb;
          a2 = Math.min(1, Math.max(0, (cr * fr + cg * fgc + cb * fb) / f2));
          e1 = cr - a2 * fr; e2 = cg - a2 * fgc; e3 = cb - a2 * fb; res = e1 * e1 + e2 * e2 + e3 * e3;
          if (res < bestRes - 1e-6 || (Math.abs(res - bestRes) <= 1e-6 && a2 > bestA)) { bestRes = res; bestA = a2; }
        }
      }
      if (bestA < 0) return ramp(dist[q]);
      if (bestRes > 40 * 40) return bg[q] ? ramp(dist[q]) : 1;
      return bestA >= 0.985 ? 1 : bestA;
    }
    for (p = 0; p < n; p++) {
      if (bg[p]) alpha[p] = nearFg[p] ? estimate(p) : 0;
      else alpha[p] = nearBg[p] ? estimate(p) : 1;
    }

    // 4. take the backdrop colour out of soft px (C = a*F + (1-a)*B  ->  F = (C - (1-a)*B) / a)
    for (p = 0; p < n; p++) {
      var a = alpha[p];
      i = p * 4;
      if (a >= 1) continue;
      if (a <= 0) { d[i + 3] = 0; continue; }
      d[i] = Math.max(0, Math.min(255, (d[i] - (1 - a) * R) / a));
      d[i + 1] = Math.max(0, Math.min(255, (d[i + 1] - (1 - a) * G) / a));
      d[i + 2] = Math.max(0, Math.min(255, (d[i + 2] - (1 - a) * B) / a));
      d[i + 3] = Math.round(a * d[i + 3]);
    }

    // 5. drop tiny opaque specks
    despeck(d, w, h, o);

    // 6. feather: light 3x3 smoothing of alpha on the edge only; px that become visible take their colour from the art
    if (o.feather > 0) {
      var A0 = new Uint8Array(n);
      for (p = 0; p < n; p++) A0[p] = d[p * 4 + 3];
      for (var y = 1; y < h - 1; y++) {
        for (x = 1; x < w - 1; x++) {
          p = y * w + x;
          var c0 = A0[p], l = A0[p - 1], r = A0[p + 1], u = A0[p - w], dn = A0[p + w];
          if (c0 === l && c0 === r && c0 === u && c0 === dn) continue;
          var ul = A0[p - w - 1], ur = A0[p - w + 1], dl = A0[p + w - 1], dr2 = A0[p + w + 1];
          var na = (4 * c0 + 2 * (l + r + u + dn) + ul + ur + dl + dr2) / 16;
          if (c0 > 0 && na > c0) continue; // smoothing only softens, never thickens art px
          i = p * 4;
          if (c0 === 0) {
            // newly visible px: colour from the visible neighbours (weighted by their alpha)
            var sw = 0, sr = 0, sg = 0, sb = 0, nbs = [p - 1, p + 1, p - w, p + w, p - w - 1, p - w + 1, p + w - 1, p + w + 1];
            for (t = 0; t < 8; t++) { var qq = nbs[t], wa = A0[qq]; if (wa) { var qi = qq * 4; sw += wa; sr += wa * d[qi]; sg += wa * d[qi + 1]; sb += wa * d[qi + 2]; } }
            if (!sw) continue;
            na = Math.min(na, 96);
            d[i] = sr / sw; d[i + 1] = sg / sw; d[i + 2] = sb / sw;
          }
          d[i + 3] = Math.round(na);
        }
      }
    }

    for (p = 0; p < n; p++) { k = d[p * 4 + 3]; if (k === 0) stats.removed++; else if (k < 255) stats.semi++; }
    stats.removed /= n;
    return stats;
  }

  function bbox(img, thr) {
    var w = img.width, h = img.height, d = img.data, x0 = w, y0 = h, x1 = -1, y1 = -1, x, y;
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) if (d[(y * w + x) * 4 + 3] > thr) {
      if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
    }
    return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
  }

  function cut(source, opts) {
    var o = opt(opts);
    return load(source).then(function (el) {
      var s = sizeOf(el);
      if (!s.w || !s.h) throw new Error('empty image');
      var k = o.maxSide > 0 ? Math.min(1, o.maxSide / Math.max(s.w, s.h)) : 1;
      var w = Math.max(1, Math.round(s.w * k)), h = Math.max(1, Math.round(s.h * k));
      var c = mk(w, h), x = ctx2d(c);
      x.drawImage(el, 0, 0, w, h);
      var img = x.getImageData(0, 0, w, h);
      var st = cutImageData(img, o);
      var box = o.trim ? bbox(img, 8) : { x: 0, y: 0, w: w, h: h };
      if (!box) box = { x: 0, y: 0, w: w, h: h };
      var pad = o.trim ? Math.max(0, o.pad | 0) : 0;
      var bx = Math.max(0, box.x - pad), by = Math.max(0, box.y - pad);
      var bw = Math.min(w, box.x + box.w + pad) - bx, bh = Math.min(h, box.y + box.h + pad) - by;
      x.putImageData(img, 0, 0);
      var out = mk(bw, bh);
      ctx2d(out).putImageData(x.getImageData(bx, by, bw, bh), 0, 0);
      if (el && el.close) { try { el.close(); } catch (e) {} }
      var sc = Math.max(1, Math.min(8, +o.scale || 1));
      if (sc > 1) {
        var big = mk(Math.round(bw * sc), Math.round(bh * sc)), bx2 = ctx2d(big);
        bx2.imageSmoothingEnabled = true; try { bx2.imageSmoothingQuality = 'high'; } catch (e) {}
        bx2.drawImage(out, 0, 0, big.width, big.height);
        out = big;
      }
      return { canvas: out, w: out.width, h: out.height, scale: sc, srcW: s.w, srcH: s.h, bbox: { x: bx / k, y: by / k, w: bw / k, h: bh / k },
        plain: st.plain, hadAlpha: st.hadAlpha, removed: st.removed, ref: st.ref, uniform: st.uniform, semi: st.semi, opts: o };
    });
  }

  function alphaStats(c) {
    var w = c.width, h = c.height, d = ctx2d(c).getImageData(0, 0, w, h).data, r = { total: w * h, transparent: 0, opaque: 0, semi: 0 };
    for (var i = 3; i < d.length; i += 4) { if (d[i] === 0) r.transparent++; else if (d[i] === 255) r.opaque++; else r.semi++; }
    r.corners = [d[3], d[(w - 1) * 4 + 3], d[((h - 1) * w) * 4 + 3], d[(h * w - 1) * 4 + 3]];
    return r;
  }

  window.ArdorDtfCutout = { version: 1, DEFAULTS: DEFAULTS, cut: cut, cutImageData: cutImageData, alphaStats: alphaStats, load: load };
})();
